/**
 * History Page
 * View past automation runs and generated designs
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Divider,
  Tooltip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import AppLayout from '../components/Layout/AppLayout';
import HorizontalProgressStepper from '../components/HorizontalProgressStepper';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import Toast from '../components/Common/Toast';
import api from '../services/api';
import { intervalManager } from '../utils/intervalManager';

/**
 * Renders caption text with **bold** markdown converted to <strong> elements.
 * Each line is preserved, and **text** segments are rendered as bold.
 */
function renderCaptionText(text) {
  if (!text) return null;
  return text.split('\n').map((line, lineIdx) => {
    const parts = line.split(/\*\*(.+?)\*\*/g);
    return (
      <React.Fragment key={lineIdx}>
        {parts.map((part, i) =>
          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
        )}
        {'\n'}
      </React.Fragment>
    );
  });
}

// Platform icon and color config
const platformConfig = {
  linkedin: { icon: LinkedInIcon, color: '#0A66C2', label: 'LinkedIn' },
  facebook: { icon: FacebookIcon, color: '#1877F2', label: 'Facebook' },
  instagram: { icon: InstagramIcon, color: '#E4405F', label: 'Instagram' },
  external_flyer: { icon: ImageIcon, color: '#666', label: 'External Flyer' },
};

export default function HistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Load cached data from sessionStorage on mount
  const getCachedData = () => {
    try {
      const cached = sessionStorage.getItem('historyData');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const [historyData, setHistoryData] = useState(getCachedData());
  const [loading, setLoading] = useState(!getCachedData());
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [copiedCaptionId, setCopiedCaptionId] = useState(null);
  const [expandedCaptions, setExpandedCaptions] = useState({});
  // Track selected platform per run: { runId: 'linkedin' }
  const [selectedPlatforms, setSelectedPlatforms] = useState({});
  // Regeneration dialog state
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedDesignForRegen, setSelectedDesignForRegen] = useState(null);
  const [selectedReqNumberForRegen, setSelectedReqNumberForRegen] = useState(null);
  const [regenerationType, setRegenerationType] = useState('text_and_image');
  const [regenImageStyle, setRegenImageStyle] = useState('real_people');
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingCaption, setRegeneratingCaption] = useState(null); // platform being regenerated
  const [progressData, setProgressData] = useState({});  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [myRunsOnly, setMyRunsOnly] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [expandedRuns, setExpandedRuns] = useState({});
  const [showAllLoading, setShowAllLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ open: true, message, onConfirm });
  };
  const firstMatchRef = useRef(null);
  const hasAutoScrolled = useRef(false);
  // Compute job filter from URL immediately — must be before any useEffect that references it
  const jobIdFilter = searchParams.get('job') ? parseInt(searchParams.get('job')) : null;
  const [filters, setFilters] = useState({
    jobName: '',
    dateFrom: '',
    dateTo: '',
  });

  const handleCopyLink = (link, designId) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(designId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleCopyCaption = (caption, designId) => {
    navigator.clipboard.writeText(caption).then(() => {
      setCopiedCaptionId(designId);
      setTimeout(() => setCopiedCaptionId(null), 2000);
    });
  };

  const handleRegenerateCaption = async (designId, platform) => {
    setRegeneratingCaption(platform);
    try {
      const result = await api.post(`/automation/design/${designId}/regenerate-caption`);
      showSnackbar(`Caption regenerated for ${platform}!`);
      fetchHistory();
    } catch (err) {
      showSnackbar('Failed to regenerate caption: ' + (err.response?.data?.detail || err.message), 'error');
      console.error(err);
    } finally {
      setRegeneratingCaption(null);
    }
  };

  const toggleCaptionExpand = (designId) => {
    setExpandedCaptions((prev) => ({ ...prev, [designId]: !prev[designId] }));
  };

  const handlePlatformSelect = (runId, platform) => {
    setSelectedPlatforms((prev) => ({ ...prev, [runId]: platform }));
  };

  const handleOpenRegenerateDialog = (design, requisitionNumber) => {
    setSelectedDesignForRegen(design);
    setSelectedReqNumberForRegen(requisitionNumber || null);
    setRegenerationType('text_and_image');
    setRegenImageStyle('real_people');
    setRegenerateDialogOpen(true);
  };

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setSelectedDesignForRegen(null);
    setSelectedReqNumberForRegen(null);
  };

  const handleRegenerateDesign = async () => {
    if (!selectedDesignForRegen) return;

    setRegenerating(true);
    try {
      const response = await api.post(`/automation/regenerate/${selectedDesignForRegen.id}`, {
        regeneration_type: regenerationType,
        image_style: regenImageStyle,
        requisition_number: selectedReqNumberForRegen,
      });

      // Optimistic update: Add the pending design to the UI immediately
      const newDesign = response.data;
      setHistoryData(prevData => {
        if (!prevData || !prevData.history) return prevData;

        const updatedHistory = prevData.history.map(run => {
          if (run.run_id === newDesign.run_id) {
            return {
              ...run,
              designs: [...(run.designs || []), {
                id: newDesign.id,
                platform: newDesign.platform,
                version: newDesign.version,
                template_id: newDesign.template_id,
                template_name: newDesign.template_name,
                canva_design_id: newDesign.canva_design_id,
                canva_edit_url: newDesign.canva_edit_url,
                caption: newDesign.caption,
                apply_link: newDesign.apply_link,
                export_png_url: newDesign.export_png_url,
                generation_status: 'pending',
                created_at: new Date().toISOString(),
              }]
            };
          }
          return run;
        });

        const updatedData = {
          ...prevData,
          history: updatedHistory
        };

        // Update sessionStorage with optimistic data
        sessionStorage.setItem('historyData', JSON.stringify(updatedData));

        return updatedData;
      });

      handleCloseRegenerateDialog();

      // Poll more frequently for 2 minutes after regeneration starts
      let pollCount = 0;
      const maxPolls = 24; // 2 minutes (5 second intervals)
      const pollInterval = setInterval(() => {
        fetchHistory(true); // Silent refresh
        pollCount++;
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      }, 3000); // Poll every 3s for faster updates

    } catch (err) {
      showSnackbar('Failed to regenerate design: ' + (err.response?.data?.detail || err.message), 'error');
      console.error(err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteRun = async (runId) => {
    showConfirm('Delete this run and all its designs? This cannot be undone.', async () => {
      try {
        await api.delete(`/history/run/${runId}`);
        setHistoryData(prev => {
          if (!prev) return prev;
          const updated = { ...prev, history: prev.history.filter(r => r.run_id !== runId) };
          sessionStorage.setItem('historyData', JSON.stringify(updated));
          return updated;
        });
        showSnackbar('Run deleted successfully');
      } catch (err) {
        showSnackbar(err.response?.data?.detail || 'Failed to delete run', 'error');
      }
    });
  };

  const handleBulkDelete = async () => {
    const count = selectedRuns.size;
    showConfirm(`Delete ${count} run${count !== 1 ? 's' : ''} and all their designs? This cannot be undone.`, async () => {
      const ids = [...selectedRuns];
      const deletedIds = new Set();
      for (const runId of ids) {
        try {
          await api.delete(`/history/run/${runId}`);
          deletedIds.add(runId);
        } catch {
          // continue with others
        }
      }
      setHistoryData(prev => {
        if (!prev) return prev;
        const updated = { ...prev, history: prev.history.filter(r => !deletedIds.has(r.run_id)) };
        sessionStorage.setItem('historyData', JSON.stringify(updated));
        return updated;
      });
      setSelectedRuns(new Set());
      setSelectionMode(false);
      const failed = ids.length - deletedIds.size;
      if (failed > 0) showSnackbar(`${failed} run(s) could not be deleted.`, 'warning');
      else showSnackbar(`${deletedIds.size} run(s) deleted successfully`);
    });
  };

  const toggleRunSelection = (e, runId) => {
    e.stopPropagation();
    setSelectedRuns(prev => {
      const next = new Set(prev);
      next.has(runId) ? next.delete(runId) : next.add(runId);
      return next;
    });
  };

  // Stop/Cancel automation
  const handleStopAutomation = async (runId) => {
    showConfirm('Are you sure you want to stop this automation?', async () => {
      try {
        await api.post(`/automation/run/${runId}/cancel`);
        showSnackbar('Automation stopped successfully');
        fetchHistory();
      } catch (error) {
        console.error('Error stopping automation:', error);
        showSnackbar('Failed to stop automation: ' + (error.response?.data?.detail || error.message), 'error');
      }
    });
  };

  const handleDownloadDesign = async (imageUrl, designId, platform) => {
    try {
      // Proxy through backend to avoid Azure Blob CORS restrictions
      const token = localStorage.getItem('access_token');
      const baseUrl = process.env.REACT_APP_API_URL || (
        window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : '/api'
      );
      const response = await fetch(`${baseUrl}/history/design/${designId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `design-${designId}-${platform}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      showSnackbar('Failed to download image. Please try again.', 'error');
    }
  };

  const [dateRangeError, setDateRangeError] = useState('');

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updated = { ...prev, [field]: value };
      if (updated.dateFrom && updated.dateTo && updated.dateTo < updated.dateFrom) {
        setDateRangeError('To Date must be on or after From Date.');
      } else {
        setDateRangeError('');
      }
      return updated;
    });
  };

  const handleClearFilters = () => {
    setDateRangeError('');
    setFilters({
      jobName: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  // Track active SSE connections per run (runId -> AbortController)
  const sseConnectionsRef = useRef(new Map());
  // Track active polling intervals per run (runId -> intervalId) used as SSE fallback
  const progressPollRef = useRef(new Map());

  const baseUrl = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : '/api'
  );

  const stopProgressPoll = useCallback((runId) => {
    const intervalId = progressPollRef.current.get(runId);
    if (intervalId) {
      clearInterval(intervalId);
      progressPollRef.current.delete(runId);
    }
  }, []);

  // Polling fallback: called when SSE fails or disconnects mid-run
  const startProgressPoll = useCallback((runId) => {
    if (progressPollRef.current.has(runId)) return; // already polling
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const poll = async () => {
      try {
        const res = await fetch(`${baseUrl}/automation/run/${runId}/progress`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setProgressData(prev => ({ ...prev, [runId]: data }));
        if (['completed', 'failed', 'cancelled'].includes(data.run_status)) {
          stopProgressPoll(runId);
          fetchHistory(true);
        }
      } catch (_) {}
    };

    poll(); // immediate first call
    const id = setInterval(poll, 2000);
    progressPollRef.current.set(runId, id);
  }, [baseUrl, stopProgressPoll]);

  // Connect progress tracking for a single run.
  // Polling is the primary mechanism (reliable behind Azure ARR proxy).
  // SSE is attempted in parallel as a real-time supplement — if it delivers
  // data, polling stops; if it fails or stalls, polling continues unaffected.
  const connectSSE = useCallback((runId) => {
    if (sseConnectionsRef.current.has(runId)) return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Always start polling immediately — guaranteed to work on Azure
    startProgressPoll(runId);

    const controller = new AbortController();
    sseConnectionsRef.current.set(runId, controller);

    (async () => {
      try {
        const response = await fetch(`${baseUrl}/automation/run/${runId}/progress/stream`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          sseConnectionsRef.current.delete(runId);
          return; // polling already running
        }

        // SSE connected and streaming — stop the poll (SSE is more efficient)
        stopProgressPoll(runId);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.__done) {
                  fetchHistory(true);
                  sseConnectionsRef.current.delete(runId);
                  return;
                }
                setProgressData(prev => ({ ...prev, [runId]: data }));
              } catch (parseErr) {
                // Skip malformed data
              }
            }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          // SSE dropped — polling already covers this
        }
      } finally {
        sseConnectionsRef.current.delete(runId);
        // Restart polling if SSE dropped mid-run
        startProgressPoll(runId);
      }
    })();
  }, [baseUrl, startProgressPoll, stopProgressPoll]);

  useEffect(() => {
    // If we have cached data, fetch silently in background (no loading spinner)
    const hasCachedData = !!getCachedData();
    fetchHistory(hasCachedData);

    // Poll history every 30 seconds for design/status updates
    const interval = intervalManager.register(
      setInterval(() => {
        fetchHistory(true);
      }, 30000)
    );
    return () => intervalManager.unregister(interval);
  }, []);

  // Start SSE connections for active runs when historyData changes
  useEffect(() => {
    if (!historyData || !historyData.history) return;

    const activeRunIds = historyData.history
      .filter(run => run.run_status === 'in_progress')
      .map(run => run.run_id);

    activeRunIds.forEach(runId => connectSSE(runId));

    // Clean up progress polling for runs that are no longer in_progress
    progressPollRef.current.forEach((_, runId) => {
      if (!activeRunIds.includes(runId)) stopProgressPoll(runId);
    });
  }, [historyData, connectSSE, stopProgressPoll]);

  // Cleanup all SSE connections and polls on unmount
  useEffect(() => {
    return () => {
      sseConnectionsRef.current.forEach(controller => controller.abort());
      sseConnectionsRef.current.clear();
      progressPollRef.current.forEach(id => clearInterval(id));
      progressPollRef.current.clear();
    };
  }, []);

  // Reset scroll lock when job filter changes (new navigation)
  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [jobIdFilter]);

  // Auto-expand runs for a specific job when arriving from dashboard "Existing Designs" link.
  // Only scrolls once — hasAutoScrolled prevents re-triggering on every 5s poll.
  useEffect(() => {
    if (!jobIdFilter || !historyData?.history) return;
    const matchingRunIds = historyData.history
      .filter(run => run.job?.id === jobIdFilter)
      .map(run => run.run_id);
    if (matchingRunIds.length === 0) return;
    setExpandedRuns(prev => {
      const next = { ...prev };
      matchingRunIds.forEach(id => { next[id] = true; });
      return next;
    });
    if (!hasAutoScrolled.current) {
      hasAutoScrolled.current = true;
      setTimeout(() => {
        if (firstMatchRef.current) {
          firstMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [jobIdFilter, historyData]);
  const fetchHistory = async (silentRefresh = false, jobIdOverride = undefined) => {
    // Don't show loading spinner on background refreshes
    if (!silentRefresh) {
      setLoading(true);
    }

    try {
      const jobParam = jobIdOverride !== undefined
        ? jobIdOverride
        : new URLSearchParams(window.location.search).get('job');
      const url = jobParam ? `/history/?job_id=${jobParam}` : '/history/';
      const response = await api.get(url);
      setHistoryData(response.data);
      setCurrentUserId(response.data.current_user_id);
      // Cache data for instant navigation
      sessionStorage.setItem('historyData', JSON.stringify(response.data));
      setError(''); // Clear any previous errors
    } catch (err) {
      if (!silentRefresh) {
        setError('Failed to load history');
      }
      console.error(err);
    } finally {
      if (!silentRefresh) {
        setLoading(false);
      }
      setShowAllLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
      case 'cancelled':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <PendingIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'in_progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Group designs by platform
  const groupDesignsByPlatform = (designs) => {
    const grouped = {};
    (designs || []).forEach((design) => {
      if (!grouped[design.platform]) {
        grouped[design.platform] = [];
      }
      grouped[design.platform].push(design);
    });
    return grouped;
  };

  const history = historyData?.history || [];

  const jobFilterTitle = useMemo(() => {
    if (!jobIdFilter) return null;
    const match = history.find(run => run.job?.id === jobIdFilter);
    return match?.job?.requisition_title || `Job #${jobIdFilter}`;
  }, [jobIdFilter, history]);

  // Only show full-screen loading if there's no data yet
  if (loading && !historyData) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error && !historyData) {
    return (
      <AppLayout>
        <Alert severity="error">{error}</Alert>
      </AppLayout>
    );
  }

  // Parse backend UTC timestamps correctly (backend returns no Z suffix)
  const parseUTC = (dateStr) => {
    if (!dateStr) return new Date(0);
    const s = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
    return new Date(s);
  };

  // Filter history based on filter criteria
  const filteredHistory = history.filter((run) => {
    if (jobIdFilter && run.job?.id !== jobIdFilter) return false;

    const normalizeSearch = (s) => (s || '').toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
    const matchesJobName = !filters.jobName ||
      normalizeSearch(run.job?.requisition_title).includes(normalizeSearch(filters.jobName));

    const runDate = parseUTC(run.created_at);
    const matchesDateFrom = !filters.dateFrom || dateRangeError ||
      runDate >= new Date(filters.dateFrom);
    const matchesDateTo = !filters.dateTo || dateRangeError ||
      runDate <= new Date(new Date(filters.dateTo).setHours(23, 59, 59, 999));

    return matchesJobName && matchesDateFrom && matchesDateTo;
  }).filter(run => !myRunsOnly || run.run_by?.id === currentUserId);

  return (
    <AppLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Automation History
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View your past automation runs and generated designs
          </Typography>
        </Box>

        {/* Job filter banner (from dashboard "Existing Designs" click) */}
        {jobIdFilter && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={showAllLoading}
                startIcon={showAllLoading ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={() => {
                  setShowAllLoading(true);
                  setSearchParams({});
                  fetchHistory(false, null);
                }}
              >
                {showAllLoading ? 'Loading...' : 'Show All'}
              </Button>
            }
          >
            Showing designs for: <strong>{jobFilterTitle}</strong>
          </Alert>
        )}

        {/* Filter Controls */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Row 1: action buttons + run count */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              size="small"
              startIcon={showFilters ? <ClearIcon /> : <FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant={myRunsOnly ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setMyRunsOnly(!myRunsOnly)}
              color="secondary"
            >
              {myRunsOnly ? 'All Runs' : 'My Runs Only'}
            </Button>
            <Button
              variant={selectionMode ? 'contained' : 'outlined'}
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => { setSelectionMode(prev => !prev); setSelectedRuns(new Set()); }}
            >
              {selectionMode ? 'Cancel Select' : 'Select to Delete'}
            </Button>
            <Chip
              label={`${filteredHistory.length} of ${history.length} runs`}
              color="primary"
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
          </Box>
          {/* Row 2: filter inputs (only when visible) */}
          {showFilters && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Job Name"
                placeholder="Filter by job name..."
                value={filters.jobName}
                onChange={(e) => handleFilterChange('jobName', e.target.value)}
                sx={{ flex: '1 1 220px', minWidth: 180 }}
              />
              <TextField
                size="small"
                label="From Date"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 160px', minWidth: 140 }}
              />
              <TextField
                size="small"
                label="To Date"
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!dateRangeError}
                helperText={dateRangeError || ''}
                sx={{ flex: '1 1 160px', minWidth: 140 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
          )}
        </Box>

        {/* Bulk action bar */}
        {selectionMode && (() => {
          const ownRuns = filteredHistory.filter(r => r.run_by?.id === currentUserId);
          const allOwnSelected = ownRuns.length > 0 && ownRuns.every(r => selectedRuns.has(r.run_id));
          const someSelected = ownRuns.some(r => selectedRuns.has(r.run_id));
          return (
            <Paper elevation={2} sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Checkbox
                checked={allOwnSelected}
                indeterminate={someSelected && !allOwnSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRuns(new Set(ownRuns.map(r => r.run_id)));
                  } else {
                    setSelectedRuns(new Set());
                  }
                }}
                color="error"
              />
              <Typography variant="body2" fontWeight={500}>
                {selectedRuns.size > 0 ? `${selectedRuns.size} run${selectedRuns.size !== 1 ? 's' : ''} selected` : 'Select your runs to delete'}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                disabled={selectedRuns.size === 0}
                onClick={handleBulkDelete}
                sx={{ ml: 'auto' }}
              >
                Delete Selected ({selectedRuns.size})
              </Button>
            </Paper>
          );
        })()}

        {/* History List */}
        {filteredHistory.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
            <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {history.length === 0 ? 'No automation history yet' : 'No runs match the filter criteria'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {history.length === 0 ? 'Your automation runs will appear here' : 'Try adjusting your filters'}
            </Typography>
          </Paper>
        ) : (
          filteredHistory.map((run, index) => {
            const groupedDesigns = groupDesignsByPlatform(run.designs);
            const platforms = Object.keys(groupedDesigns);
            const activePlatform = selectedPlatforms[run.run_id] || platforms[0] || null;
            const activeDesigns = activePlatform ? (groupedDesigns[activePlatform] || []) : [];
            const isExpanded = expandedRuns[run.run_id] ?? false;
            const isFirstMatch = jobIdFilter && run.job?.id === jobIdFilter && index === filteredHistory.findIndex(r => r.job?.id === jobIdFilter);

            return (
              <Box key={run.run_id} ref={isFirstMatch ? firstMatchRef : null}>
              <Accordion
                sx={{ mb: 2 }}
                elevation={2}
                expanded={isExpanded}
                onChange={(_, open) => setExpandedRuns(prev => ({ ...prev, [run.run_id]: open }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    {selectionMode && (
                      <Checkbox
                        checked={selectedRuns.has(run.run_id)}
                        disabled={run.run_by?.id !== currentUserId}
                        onClick={(e) => toggleRunSelection(e, run.run_id)}
                        color="error"
                        size="small"
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(run.run_status)}
                      <Typography variant="h6">
                        {run.job?.requisition_title || 'Unknown Job'}
                      </Typography>
                    </Box>
                    <Chip
                      label={run.run_status}
                      color={getStatusColor(run.run_status)}
                      size="small"
                    />
                    {run.run_by && (
                      <Chip
                        label={run.run_by.full_name || run.run_by.email}
                        size="small"
                        variant="outlined"
                        color={run.run_by.id === currentUserId ? 'primary' : 'default'}
                        sx={{ maxWidth: 160 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {parseUTC(run.created_at).toLocaleString()}
                    </Typography>
                    {run.run_by?.id === currentUserId && (
                      <Tooltip title="Delete this run">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); handleDeleteRun(run.run_id); }}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Job Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Job Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Requisition #:</strong> {run.job?.requisition_number}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Department:</strong> {run.job?.department}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Location:</strong> {run.job?.location}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Platforms:</strong> {run.platforms?.join(', ') || 'N/A'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Show progress for in-progress runs */}
                  {run.run_status === 'in_progress' && (
                    <Box sx={{ mb: 3 }}>
                      <HorizontalProgressStepper runId={run.run_id} progressData={progressData[run.run_id]} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="outlined" color="error" size="small" onClick={() => handleStopAutomation(run.run_id)}>
                          ⏹ Stop Automation
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Platform Selector Icons */}
                  {platforms.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Generated Designs ({run.designs?.length || 0})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                        {platforms.map((platform) => {
                          const config = platformConfig[platform] || {
                            icon: ImageIcon,
                            color: '#666',
                            label: platform,
                          };
                          const PlatformIcon = config.icon;
                          const isActive = activePlatform === platform;
                          const designCount = groupedDesigns[platform]?.length || 0;

                          return (
                            <Tooltip
                              key={platform}
                              title={`${config.label} (${designCount} design${designCount !== 1 ? 's' : ''})`}
                            >
                              <Box
                                onClick={() => handlePlatformSelect(run.run_id, platform)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  px: 2,
                                  py: 1,
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  border: '2px solid',
                                  borderColor: isActive ? config.color : 'transparent',
                                  bgcolor: isActive ? `${config.color}10` : 'grey.50',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: `${config.color}15`,
                                    borderColor: config.color,
                                  },
                                }}
                              >
                                <PlatformIcon
                                  sx={{
                                    fontSize: 28,
                                    color: isActive ? config.color : 'grey.500',
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={isActive ? 600 : 400}
                                    sx={{ color: isActive ? config.color : 'text.primary' }}
                                  >
                                    {config.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {designCount} design{designCount !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Active Platform Header & Caption */}
                  {activePlatform && activeDesigns.length > 0 && (() => {
                    const config = platformConfig[activePlatform] || {
                      icon: ImageIcon,
                      color: '#666',
                      label: activePlatform,
                    };
                    const PlatformIcon = config.icon;
                    const platformCaption = activeDesigns[0]?.caption; // Caption is same for all designs in platform

                    return (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            pb: 1,
                            borderBottom: `2px solid ${config.color}`,
                          }}
                        >
                          <PlatformIcon sx={{ color: config.color, fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: config.color }}>
                            {config.label} Designs
                          </Typography>
                        </Box>

                        {/* Platform Caption - hidden for external_flyer (no social caption needed) */}
                        {activePlatform !== 'external_flyer' && platformCaption ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mb: 3,
                              bgcolor: 'grey.50',
                              borderRadius: 2,
                              borderLeft: `4px solid ${config.color}`,
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PlatformIcon sx={{ color: config.color, fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: config.color }}>
                                  Social Media Caption
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {run.run_by?.id === currentUserId && (
                                  <Tooltip title="Regenerate Caption">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => showConfirm('Regenerate the caption for this platform? The current caption will be replaced.', () => handleRegenerateCaption(activeDesigns[0]?.id, activePlatform))}
                                        disabled={regeneratingCaption === activePlatform}
                                        sx={{ color: config.color }}
                                      >
                                        {regeneratingCaption === activePlatform ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <RefreshIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <Tooltip title={copiedCaptionId === activePlatform ? 'Copied!' : 'Copy Caption'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyCaption(platformCaption, activePlatform)}
                                    sx={{
                                      color: copiedCaptionId === activePlatform ? 'success.main' : config.color,
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {platformCaption.length > 200 && (
                                  <Tooltip title={expandedCaptions[activePlatform] ? 'Show Less' : 'Show More'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleCaptionExpand(activePlatform)}
                                      sx={{ color: config.color }}
                                    >
                                      {expandedCaptions[activePlatform] ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            <Divider sx={{ mb: 1.5 }} />
                            <Collapse in={expandedCaptions[activePlatform] || platformCaption.length <= 200} collapsedSize={60}>
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  color: 'text.primary',
                                  lineHeight: 1.7,
                                }}
                              >
                                {renderCaptionText(platformCaption)}
                              </Typography>
                            </Collapse>
                            {!expandedCaptions[activePlatform] && platformCaption.length > 200 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 1,
                                  cursor: 'pointer',
                                  color: config.color,
                                  fontWeight: 500,
                                }}
                                onClick={() => toggleCaptionExpand(activePlatform)}
                              >
                                Click to read more...
                              </Typography>
                            )}
                            {/* Apply Link */}
                            {activeDesigns[0]?.apply_link && (
                              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  Apply Link:
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {activeDesigns[0].apply_link}
                                </Typography>
                                <Button
                                  size="small"
                                  variant={copiedId === `link-${activePlatform}` ? 'contained' : 'outlined'}
                                  color={copiedId === `link-${activePlatform}` ? 'success' : 'inherit'}
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => handleCopyLink(activeDesigns[0].apply_link, `link-${activePlatform}`)}
                                  sx={{ minWidth: 'auto', fontSize: '0.7rem' }}
                                >
                                  {copiedId === `link-${activePlatform}` ? 'Copied!' : 'Copy'}
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        ) : activePlatform !== 'external_flyer' && run.run_status === 'completed' && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mb: 3,
                              bgcolor: 'grey.50',
                              borderRadius: 2,
                              borderLeft: `4px solid ${config.color}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PlatformIcon sx={{ color: config.color, fontSize: 20 }} />
                              <Typography variant="body2" color="text.secondary">
                                No caption generated yet
                              </Typography>
                            </Box>
                            {run.run_by?.id === currentUserId && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={regeneratingCaption === activePlatform ? <CircularProgress size={16} /> : <RefreshIcon />}
                                onClick={() => showConfirm('Generate a caption for this platform? This will create a new caption.', () => handleRegenerateCaption(activeDesigns[0]?.id, activePlatform))}
                                disabled={regeneratingCaption === activePlatform}
                                sx={{ color: config.color, borderColor: config.color }}
                              >
                                Generate Caption
                              </Button>
                            )}
                          </Paper>
                        )}
                      </>
                    );
                  })()}

                  {/* Designs - Horizontal scroll for the active platform */}
                  {activeDesigns.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        overflowX: 'auto',
                        pb: 2,
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: 'grey.300',
                          borderRadius: 3,
                        },
                      }}
                    >
                      {activeDesigns.map((design) => (
                        <Card
                          key={design.id}
                          elevation={1}
                          sx={{
                            minWidth: 300,
                            maxWidth: 340,
                            flex: '0 0 auto',
                            opacity: design.generation_status === 'pending' ? 0.7 : 1,
                            position: 'relative',
                          }}
                        >
                          {design.generation_status === 'pending' && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                zIndex: 10,
                              }}
                            >
                              <CircularProgress size={60} />
                            </Box>
                          )}
                          {design.export_png_url && design.generation_status !== 'pending' && (
                            <CardMedia
                              component="img"
                              height="200"
                              image={design.export_png_url}
                              alt={`${design.platform} design`}
                              sx={{
                                objectFit: 'cover',
                                cursor: 'pointer',
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: 0.85 }
                              }}
                              onClick={() => window.open(design.export_png_url, '_blank')}
                            />
                          )}
                          {(!design.export_png_url || design.generation_status === 'pending') && (
                            <Box
                              sx={{
                                height: 200,
                                bgcolor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                            </Box>
                          )}
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {design.template_name}
                              </Typography>
                              <Chip
                                label={`v${design.version}`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                            <Chip
                              label={design.generation_status}
                              size="small"
                              color={getStatusColor(design.generation_status)}
                            />

                            <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                              {run.run_by?.id === currentUserId && (
                                <Tooltip title={design.generation_status === 'pending' ? 'Design is generating...' : 'Regenerate Design'}>
                                  <span>
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleOpenRegenerateDialog(design, run.job?.requisition_number)}
                                      disabled={design.generation_status === 'pending'}
                                      sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                        '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                                      }}
                                    >
                                      <RefreshIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {design.apply_link && design.generation_status === 'completed' && (
                                <Button
                                  size="small"
                                  variant={copiedId === design.id ? 'contained' : 'outlined'}
                                  color={copiedId === design.id ? 'success' : 'secondary'}
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => handleCopyLink(design.apply_link, design.id)}
                                >
                                  {copiedId === design.id ? 'Copied!' : 'Copy Apply Link'}
                                </Button>
                              )}
                              {design.canva_edit_url && design.generation_status === 'completed' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  href={design.canva_edit_url}
                                  target="_blank"
                                >
                                  Edit
                                </Button>
                              )}
                              {design.export_png_url && design.generation_status === 'completed' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => handleDownloadDesign(design.export_png_url, design.id, design.platform)}
                                >
                                  Download
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    run.designs?.length === 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No designs generated for this run
                      </Alert>
                    )
                  )}

                  {run.error_message && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <strong>Error:</strong> {run.error_message}
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
              </Box>
            );
          })
        )}

        {/* Regeneration Dialog */}
        <Dialog
          open={regenerateDialogOpen}
          onClose={handleCloseRegenerateDialog}
          maxWidth="sm"
          fullWidth
          slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.45)' } } }}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>Regenerate Design</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {selectedDesignForRegen && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Design:</strong> {selectedDesignForRegen.template_name} (v{selectedDesignForRegen.version})
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 3 }}>
                    <strong>Platform:</strong> {selectedDesignForRegen.platform}
                  </Typography>
                </>
              )}

              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  What would you like to regenerate?
                </FormLabel>
                <RadioGroup
                  value={regenerationType}
                  onChange={(e) => setRegenerationType(e.target.value)}
                >
                  <FormControlLabel
                    value="text_and_image"
                    control={<Radio />}
                    label="Textual content + Image (regenerate everything)"
                  />
                  <FormControlLabel
                    value="image_only"
                    control={<Radio />}
                    label="Image only (keep existing text)"
                  />
                  <FormControlLabel
                    value="text_only"
                    control={<Radio />}
                    label="Textual content only (keep existing image)"
                  />
                </RadioGroup>
              </FormControl>

              {/* Show image style selector if regenerating image */}
              {(regenerationType === 'text_and_image' || regenerationType === 'image_only') && (
                <FormControl component="fieldset" sx={{ mt: 3 }} fullWidth>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    AI Image Style
                  </FormLabel>
                  <RadioGroup
                    value={regenImageStyle}
                    onChange={(e) => setRegenImageStyle(e.target.value)}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <img
                            src="/samples/people-sample.png"
                            alt="People style sample"
                            style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            Sample: Professional team photo
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <FormControlLabel
                        value="real_people"
                        control={<Radio />}
                        label="People - Professional team photos"
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <img
                            src="/samples/abstract-sample.png"
                            alt="Abstract style sample"
                            style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            Sample: Apparel & textile close-up
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <FormControlLabel
                        value="abstract"
                        control={<Radio />}
                        label="Abstract - Apparel & textile close-ups"
                      />
                    </Tooltip>
                  </RadioGroup>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRegenerateDialog} disabled={regenerating}>
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateDesign}
              variant="contained"
              disabled={regenerating}
              startIcon={regenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              {regenerating ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Confirm Action"
        message={confirmDialog.message}
        confirmLabel="Yes, Confirm"
        cancelLabel="Cancel"
        confirmColor="error"
        onConfirm={() => {
          setConfirmDialog(d => ({ ...d, open: false }));
          confirmDialog.onConfirm?.();
        }}
        onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
      />

      <Toast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />
    </AppLayout>
  );
}
